// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Modal from '@app/features/app/components/dialogs/Modal';
import * as MfaCommands from '@app/features/auth/commands/MfaCommands';
import {BackupCodesModal} from '@app/features/auth/components/modals/BackupCodesModal';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import type {User} from '@app/features/user/models/User';
import * as FormUtils from '@app/lib/forms';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useState} from 'react';

interface BackupCodesRegenerateModalProps {
	user: User;
}
export const BackupCodesRegenerateModal = observer(({user}: BackupCodesRegenerateModalProps) => {
	const {t, i18n} = useLingui();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const handleConfirm = async () => {
		setIsSubmitting(true);
		try {
			const backupCodes = await MfaCommands.getBackupCodes(true);
			ModalCommands.pop();
			ModalCommands.update('backup-codes', () =>
				modal(() => <BackupCodesModal backupCodes={backupCodes} user={user} />),
			);
			ToastCommands.createToast({
				type: 'success',
				children: t`Backup codes regenerated`,
			});
		} catch (error) {
			FormUtils.pushApiErrorModal(i18n, error);
		} finally {
			setIsSubmitting(false);
		}
	};
	return (
		<Modal.Root size="small" centered>
			<Modal.Header title={t`Regenerate Backup Codes`} />
			<Modal.Content>
				<Modal.ContentLayout>
					<Modal.Description>
						<Trans>This will invalidate your existing backup codes and generate new ones.</Trans>
					</Modal.Description>
				</Modal.ContentLayout>
			</Modal.Content>
			<Modal.Footer>
				<Button onClick={ModalCommands.pop} variant="secondary">
					<Trans>Cancel</Trans>
				</Button>
				<Button onClick={handleConfirm} submitting={isSubmitting}>
					<Trans>Continue</Trans>
				</Button>
			</Modal.Footer>
		</Modal.Root>
	);
});
